// =============================================================================
// SIREPPAR - Corrección de atributos tras rechazo del TEP (apoderado)
// =============================================================================
// El apoderado ve la intimación de la SEP y la resolución del TEP (PDF), y puede
// modificar CUALQUIER atributo (nombre, sigla, colores, símbolo). Al guardar, el
// trámite vuelve a la SEP (fase ATRIBUTOS_PENDIENTES) para su revisión y nueva
// publicación con un plazo de oposición de 5 días.
// =============================================================================
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../../services/api';
import { useReconocimientoApoderado } from '../../hooks/useReconocimientoApoderado';

interface CorreccionData {
  numero: string;
  denominacion: string;
  sigla: string;
  colores: string[];
  simbolosPaths: string[];
  descripcionSimbolo: string;
  intimacion: { texto: string; creadoEn: string; estado: string; tipo: string } | null;
  resolucionTep: { id: string; titulo: string; descripcion: string | null } | null;
}

const TIPOS_SIMBOLO_OK = ['application/pdf', 'image/jpeg', 'image/png'];

export default function CorregirAtributosPage() {
  const navigate = useNavigate();
  const { expedienteId, fase, isLoading } = useReconocimientoApoderado();

  const [data, setData] = useState<CorreccionData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const [denominacion, setDenominacion] = useState('');
  const [sigla, setSigla] = useState('');
  const [colores, setColores] = useState<string[]>([]);
  const [nuevoColor, setNuevoColor] = useState('#1d4ed8');
  const [descripcionSimbolo, setDescripcionSimbolo] = useState('');
  const [simbolosNuevos, setSimbolosNuevos] = useState<File[]>([]);

  useEffect(() => {
    if (isLoading) return;
    if (!expedienteId || fase !== 'ATRIBUTOS_RECHAZADOS') {
      navigate('/dashboard');
      return;
    }
    (async () => {
      try {
        const res = await api.get(`/reconocimiento/${expedienteId}/correccion-atributos`);
        const d = res.data.data as CorreccionData;
        setData(d);
        setDenominacion(d.denominacion || '');
        setSigla(d.sigla || '');
        setColores(d.colores || []);
        setDescripcionSimbolo(d.descripcionSimbolo || '');
      } catch (err: any) {
        toast.error(err.response?.data?.error?.message || 'No se pudo cargar la corrección');
        navigate('/dashboard');
      } finally {
        setCargando(false);
      }
    })();
  }, [expedienteId, fase, isLoading, navigate]);

  const descargarResolucion = async () => {
    if (!data?.resolucionTep) return;
    try {
      const r = await api.get(`/documentos/${data.resolucionTep.id}/url`);
      const url = r.data.data?.url || r.data.url;
      if (url) window.open(url, '_blank');
    } catch {
      toast.error('No se pudo abrir la resolución');
    }
  };

  const agregarColor = () => {
    if (colores.length >= 5) { toast.error('Máximo 5 colores'); return; }
    if (colores.includes(nuevoColor)) return;
    setColores([...colores, nuevoColor]);
  };

  const agregarSimbolo = (file: File | undefined) => {
    if (!file) return;
    if (simbolosNuevos.length >= 3) { toast.error('Máximo 3 símbolos'); return; }
    const extOk = /\.(pdf|jpe?g|png)$/i.test(file.name);
    if (!TIPOS_SIMBOLO_OK.includes(file.type) && !extOk) {
      toast.error('El símbolo debe ser imagen (JPG/PNG) o PDF');
      return;
    }
    if (file.size > 50 * 1024 * 1024) { toast.error('El archivo supera 50 MB'); return; }
    setSimbolosNuevos([...simbolosNuevos, file]);
  };

  const guardar = async () => {
    if (!expedienteId) return;
    if (!denominacion.trim()) { toast.error('La denominación es obligatoria'); return; }
    if (colores.length === 0) { toast.error('Declará al menos un color'); return; }
    setGuardando(true);
    try {
      // Si se cargaron nuevos símbolos, se suben a S3 y reemplazan a los anteriores;
      // si no, se conservan los paths actuales.
      let simbolosPaths = data?.simbolosPaths ?? [];
      if (simbolosNuevos.length > 0) {
        const meta = simbolosNuevos.map((f) => ({ fileName: f.name, contentType: f.type }));
        const urlsResp = await api.post('/storage/upload-urls', { files: meta });
        const urls: { url: string; path: string }[] = urlsResp.data.data;
        await Promise.all(
          simbolosNuevos.map((f, i) =>
            fetch(urls[i].url, { method: 'PUT', body: f, headers: { 'Content-Type': f.type } }),
          ),
        );
        simbolosPaths = urls.map((u) => u.path);
      }

      await api.post(`/reconocimiento/${expedienteId}/corregir-atributos`, {
        denominacion: denominacion.trim(),
        sigla: sigla.trim() || null,
        colores,
        simbolosPaths,
        descripcionSimbolo: descripcionSimbolo.trim() || null,
      });
      toast.success('Atributos corregidos. El trámite vuelve a la Secretaría Electoral.');
      navigate('/dashboard');
    } catch (err: any) {
      toast.error(err.response?.data?.error?.message || 'Error al guardar la corrección');
    } finally {
      setGuardando(false);
    }
  };

  if (cargando) {
    return <div className="min-h-[40vh] flex items-center justify-center text-gray-500">Cargando…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Corregir atributos del partido</h1>
        <p className="text-sm text-gray-500 mt-1">
          Expediente N° <strong>{data?.numero}</strong> — el Tribunal Electoral rechazó los atributos.
        </p>
      </div>

      {/* Resolución del TEP (PDF) */}
      {data?.resolucionTep && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold text-red-700 uppercase tracking-wide mb-1">Resolución del Tribunal Electoral</p>
            <p className="text-sm text-gray-800">{data.resolucionTep.descripcion || data.resolucionTep.titulo}</p>
          </div>
          <button onClick={descargarResolucion} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg whitespace-nowrap">
            📄 Ver resolución
          </button>
        </div>
      )}

      {/* Formulario de atributos */}
      <div className="bg-white shadow rounded-lg p-6 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre (denominación) *</label>
          <input value={denominacion} onChange={(e) => setDenominacion(e.target.value)} maxLength={200}
            className="w-full border border-gray-300 rounded-lg p-2 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sigla</label>
          <input value={sigla} onChange={(e) => setSigla(e.target.value)} maxLength={20}
            className="w-full border border-gray-300 rounded-lg p-2 text-sm" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Colores *</label>
          <div className="flex items-center gap-2">
            <input type="color" value={nuevoColor} onChange={(e) => setNuevoColor(e.target.value)} className="h-9 w-12 border rounded" />
            <button type="button" onClick={agregarColor} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg">Agregar color</button>
          </div>
          {colores.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {colores.map((c, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-50 p-2 rounded">
                  <div className="w-6 h-6 rounded border" style={{ backgroundColor: c }} />
                  <span className="text-sm font-mono">{c}</span>
                  <button type="button" onClick={() => setColores(colores.filter((_, idx) => idx !== i))} className="text-red-500 text-sm">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border rounded-lg p-4">
          <label className="block font-medium text-gray-700 mb-2">🏷️ Símbolo (opcional)</label>
          {data && data.simbolosPaths.length > 0 && simbolosNuevos.length === 0 && (
            <p className="text-sm text-gray-500 mb-2">Actualmente hay {data.simbolosPaths.length} símbolo(s) cargado(s). Subí uno nuevo solo si querés reemplazarlo.</p>
          )}
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => { agregarSimbolo(e.target.files?.[0]); e.currentTarget.value = ''; }} className="text-sm" />
          <p className="text-xs text-gray-500 mt-1"><strong>Un (1) símbolo por archivo</strong> (no combine varios en un mismo PDF). Imagen (JPG/PNG) o PDF, hasta 50 MB. Máx. 3.</p>
          {simbolosNuevos.length > 0 && (
            <div className="mt-2 space-y-1">
              {simbolosNuevos.map((s, i) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <span className="text-sm">✓ {s.name}</span>
                  <button type="button" onClick={() => setSimbolosNuevos(simbolosNuevos.filter((_, idx) => idx !== i))} className="text-red-500">✕</button>
                </div>
              ))}
            </div>
          )}
          <label className="block font-medium text-gray-700 mb-1 mt-3">Descripción del símbolo</label>
          <textarea value={descripcionSimbolo} onChange={(e) => setDescripcionSimbolo(e.target.value)} maxLength={500} rows={3}
            className="w-full border border-gray-300 rounded-lg p-2 text-sm" />
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          Al guardar, el trámite vuelve a la Secretaría Electoral, que revisará y volverá a publicar
          los atributos con un <strong>nuevo plazo de oposición de 5 días corridos</strong> (Art. 10 Ley XII N°23).
        </div>

        <div className="flex justify-end gap-3">
          <button type="button" onClick={() => navigate('/dashboard')} className="px-4 py-2 border rounded-lg text-sm">Cancelar</button>
          <button type="button" onClick={guardar} disabled={guardando}
            className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50">
            {guardando ? 'Guardando…' : 'Guardar y enviar a la SEP'}
          </button>
        </div>
      </div>
    </div>
  );
}
